import './OrganizationEditor.scss';
import { useServices } from 'index';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useOrganizationEditorStore } from 'stores/Admin/Editors/OrganizationEditorStore';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { InstructorsTable } from '../components/InstructorsTable/InstructorsTable';
import { OrganizationDetails } from '../components/OrganizationDetails/OrganizationDetails';
import { ManageOrganization } from '../components/ManageOrganization/ManageOrganization';
import { DeleteListItemModal } from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import { CreateOrganizationPayload, UpdateOrganizationPayload } from 'types/admin/organizations';
import { useDeleteOrganization } from 'queryHooks/Admin/Organization/useDeleteOrganization';
import { useSaveOrganization } from 'queryHooks/Admin/Organization/useSaveOrganization';
import { useGetAdminOrganization } from 'queryHooks/Admin/Organization/useGetAdminOrganization';
import { OrganizationModules } from '../components/OrganizationModules/OrganizationModules';

export const OrganizationEditor = () => {
  const organizationId = useParams().organizationId;
  const isEditMode = !!organizationId;

  const organizationsEditorService = useServices().organizationsEditorService;

  useSetPageTitle({ title: isEditMode ? 'Edit Organization' : 'Add Organization' });

  const [isDeleteOrganizationModalOn, setIsDeleteOrganizationModalOn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { data: organization, isFetching } = useGetAdminOrganization(organizationId);

  const store = useOrganizationEditorStore();
  const {
    name,
    numberOfLicensesToAdd,
    membershipDurationMonth,
    isDisabled,
    instructors,
    setName,
    setNumberOfInvites,
    setMembershipDurationMonth,
    setInstructors,
    setIsDisabled,
    resetState,
  } = store;

  useEffect(() => {
    if (isFetching || !organization) {
      return;
    }

    setName(organization.name);
    setMembershipDurationMonth(organization.membershipDurationMonth);
    setNumberOfInvites(organization.numberOfInvites ?? 0);
    setInstructors(organization.instructors ?? []);
    setIsDisabled(organization.disabled ?? false);
  }, [isFetching, organization]);

  useEffect(
    () => () => {
      resetState();
    },
    []
  );

  const createOrganizationPayload: CreateOrganizationPayload = {
    name,
    numberOfInvites: numberOfLicensesToAdd ? parseInt(numberOfLicensesToAdd) : 0,
    membershipDurationMonth,
    disabled: isDisabled,
    instructors,
  };

  const editOrganizationPayload: UpdateOrganizationPayload = {
    id: organizationId!,
    name,
    numberOfInvites: numberOfLicensesToAdd ? parseInt(numberOfLicensesToAdd) : 0,
    membershipDurationMonth,
    disabled: isDisabled,
    instructors,
  };

  const deleteOrganization = useDeleteOrganization({
    onSuccess: () => setIsDeleteOrganizationModalOn(false),
  });

  const saveOrganization = useSaveOrganization(organizationId, {
    mutationFn: (payload: CreateOrganizationPayload | UpdateOrganizationPayload) =>
      isEditMode
        ? organizationsEditorService.saveEditedOrganization(payload as UpdateOrganizationPayload)
        : organizationsEditorService.saveNewOrganization(payload as CreateOrganizationPayload),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>{isEditMode ? 'Edit Organization' : 'Add Organization'}</h1>
      </div>
      <div className='admin-content-container'>
        <div className='organization-editor-container'>
          <OrganizationDetails />
          <InstructorsTable />
          <ManageOrganization
            isDisabled={isDisabled}
            hasOrganizationId={!!organizationId}
            setIsModalOn={setIsDeleteOrganizationModalOn}
            setDisabled={setIsDisabled}
          />
          {organization?.organizationSpecificModuleNames && organization?.organizationSpecificModuleNames.length > 0 && (
            <OrganizationModules organizationSpecificModuleNames={organization.organizationSpecificModuleNames} />
          )}
        </div>
        <div className='admin-footer'>
          <button
            className='styled-button primary'
            disabled={(isEditMode && isFetching) || isLoading || !name}
            onClick={() => saveOrganization.mutate(isEditMode ? editOrganizationPayload : createOrganizationPayload)}
          >
            Save changes
          </button>
        </div>

        {organizationId && isDeleteOrganizationModalOn ? (
          <DeleteListItemModal
            isModalOn={isDeleteOrganizationModalOn}
            setIsModalOn={setIsDeleteOrganizationModalOn}
            deleteAction={() => deleteOrganization.mutate(organizationId!)}
            itemName='organization'
          />
        ) : (
          <></>
        )}
      </div>
    </>
  );
};
